/**
 * Persistent CLI defaults stored in the metadata KV table.
 *
 * All defaults use metadata keys prefixed with `defaults.`:
 * - `defaults.org` — default organization slug
 * - `defaults.project` — default project slug
 * - `defaults.telemetry` — telemetry preference (`"on"` / `"off"`)
 * - `defaults.url` — Sentry instance URL (for self-hosted)
 */

import { getDatabase } from "./index.js";
import { clearMetadata, getMetadata, setMetadata } from "./utils.js";

const DEFAULTS_ORG = "defaults.org";
const DEFAULTS_PROJECT = "defaults.project";
const DEFAULTS_TELEMETRY = "defaults.telemetry";
const DEFAULTS_URL = "defaults.url";
const DEFAULTS_HEADERS = "defaults.headers";

/** All metadata keys used for defaults (for bulk operations) */
const ALL_DEFAULTS_KEYS = [
  DEFAULTS_ORG,
  DEFAULTS_PROJECT,
  DEFAULTS_TELEMETRY,
  DEFAULTS_URL,
  DEFAULTS_HEADERS,
];

/** State of all persistent defaults */
export type DefaultsState = {
  /** Default organization slug, or null if unset */
  organization: string | null;
  /** Default project slug, or null if unset */
  project: string | null;
  /** Telemetry preference: "on", "off", or null (= default enabled) */
  telemetry: "on" | "off" | null;
  /** Default Sentry instance URL, or null if unset */
  url: string | null;
  /** Custom HTTP headers for self-hosted proxy auth, or null if unset */
  headers: string | null;
};

/** Parse a raw telemetry metadata value to a typed "on" | "off" | null. */
function parseTelemetryValue(val: string | undefined): "on" | "off" | null {
  if (val === "on") {
    return "on";
  }
  if (val === "off") {
    return "off";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Getters
// ---------------------------------------------------------------------------

/** Get the default organization slug, or null if not set. */
export function getDefaultOrganization(): string | null {
  const db = getDatabase();
  const m = getMetadata(db, [DEFAULTS_ORG]);
  return m.get(DEFAULTS_ORG) ?? null;
}

/** Get the default project slug, or null if not set. */
export function getDefaultProject(): string | null {
  const db = getDatabase();
  const m = getMetadata(db, [DEFAULTS_PROJECT]);
  return m.get(DEFAULTS_PROJECT) ?? null;
}

/**
 * Get the persistent telemetry preference.
 *
 * @returns `true` if explicitly enabled, `false` if explicitly disabled,
 *   `undefined` if no preference is stored (callers should default to enabled)
 */
export function getTelemetryPreference(): boolean | undefined {
  const db = getDatabase();
  const m = getMetadata(db, [DEFAULTS_TELEMETRY]);
  const val = m.get(DEFAULTS_TELEMETRY);
  if (val === "on") {
    return true;
  }
  if (val === "off") {
    return false;
  }
  return;
}

/** Get the default Sentry instance URL, or null if not set. */
export function getDefaultUrl(): string | null {
  const db = getDatabase();
  const m = getMetadata(db, [DEFAULTS_URL]);
  return m.get(DEFAULTS_URL) ?? null;
}

/**
 * Get the default custom headers string, or null if not set.
 * Format: semicolon-separated `Name: Value` pairs.
 */
export function getDefaultHeaders(): string | null {
  const db = getDatabase();
  const m = getMetadata(db, [DEFAULTS_HEADERS]);
  return m.get(DEFAULTS_HEADERS) ?? null;
}

/**
 * Get all persistent defaults as a structured object.
 * Used by the `sentry cli defaults` show mode and JSON output.
 */
export function getAllDefaults(): DefaultsState {
  const db = getDatabase();
  const m = getMetadata(db, ALL_DEFAULTS_KEYS);
  const telVal = m.get(DEFAULTS_TELEMETRY);
  return {
    organization: m.get(DEFAULTS_ORG) ?? null,
    project: m.get(DEFAULTS_PROJECT) ?? null,
    telemetry: parseTelemetryValue(telVal),
    url: m.get(DEFAULTS_URL) ?? null,
    headers: m.get(DEFAULTS_HEADERS) ?? null,
  };
}

// ---------------------------------------------------------------------------
// Setters (null = clear the value)
// ---------------------------------------------------------------------------

/** Set or clear the default organization. Pass `null` to clear. */
export function setDefaultOrganization(value: string | null): void {
  const db = getDatabase();
  if (value === null) {
    clearMetadata(db, [DEFAULTS_ORG]);
  } else {
    setMetadata(db, { [DEFAULTS_ORG]: value });
  }
}

/** Set or clear the default project. Pass `null` to clear. */
export function setDefaultProject(value: string | null): void {
  const db = getDatabase();
  if (value === null) {
    clearMetadata(db, [DEFAULTS_PROJECT]);
  } else {
    setMetadata(db, { [DEFAULTS_PROJECT]: value });
  }
}

/**
 * Set or clear the persistent telemetry preference.
 * Pass `null` to remove the preference (callers will default to enabled).
 */
export function setTelemetryPreference(enabled: boolean | null): void {
  const db = getDatabase();
  if (enabled === null) {
    clearMetadata(db, [DEFAULTS_TELEMETRY]);
  } else {
    setMetadata(db, { [DEFAULTS_TELEMETRY]: enabled ? "on" : "off" });
  }
}

/** Set or clear the default Sentry instance URL. Pass `null` to clear. */
export function setDefaultUrl(url: string | null): void {
  const db = getDatabase();
  if (url === null) {
    clearMetadata(db, [DEFAULTS_URL]);
  } else {
    setMetadata(db, { [DEFAULTS_URL]: url });
  }
}

/**
 * Set or clear the default custom headers. Pass `null` to clear.
 * Value should be semicolon-separated `Name: Value` pairs.
 */
export function setDefaultHeaders(value: string | null): void {
  const db = getDatabase();
  if (value === null) {
    clearMetadata(db, [DEFAULTS_HEADERS]);
  } else {
    setMetadata(db, { [DEFAULTS_HEADERS]: value });
  }
}

// ---------------------------------------------------------------------------
// Bulk operations
// ---------------------------------------------------------------------------

/** Clear all persistent defaults (org, project, telemetry, url). */
export function clearAllDefaults(): void {
  const db = getDatabase();
  clearMetadata(db, ALL_DEFAULTS_KEYS);
}
